import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { ReportTab } from '../database/schema.js';
import { zql } from '../schema.js';
import { assertCan, EventLogs } from '../utils.js';
import { addEventLog, EventLogResources } from './util.js';
/** Loose shape: Zero requires mutator args to be JSON, and the tab's filters are normalized over
 * the current defaults when read, so they are stored verbatim as JSON. */
const FiltersSchema = z.record(z.string(), z.json());
export const SaveReportViewSchema = z.object({
    tab: ReportTab,
    name: z.string().trim().nonempty(),
    filters: FiltersSchema,
    fieldIds: z.array(z.string()),
    /** a new view's client-generated id, so the address can point at it right away */
    id: z.uuidv7().optional(),
    /** the id of the view to overwrite when the name already exists in the tab */
    replaceId: z.uuidv7().optional(),
});
export const UpdateReportViewSchema = z.object({
    id: z.uuidv7(),
    filters: FiltersSchema,
    fieldIds: z.array(z.string()),
});
export const DeleteReportViewSchema = z.object({ id: z.uuidv7() });
export const ReorderReportViewsSchema = z.object({
    tab: ReportTab,
    ids: z.array(z.uuidv7()),
});
/** The view's audit details; the event log only stores flat string maps. */
function viewDetails(view) {
    return {
        id: view.id,
        tab: view.tab,
        name: view.name,
    };
}
async function findByTabAndName(tx, tab, name) {
    return await tx.run(zql.reportViews.where('tab', '=', tab).where('name', '=', name).where('deletedAt', 'IS', null).one());
}
async function nextPosition(tx, tab) {
    const last = await tx.run(zql.reportViews.where('tab', '=', tab).where('deletedAt', 'IS', null).orderBy('position', 'desc').limit(1).one());
    return (last?.position ?? -1) + 1;
}
export const reportViews = {
    /**
     * Save from the Filters dialog: overwrite the view with that name in the tab when replaceId is
     * given (the "replace existing?" confirmation), otherwise create a new one.
     */
    save: defineMutator(SaveReportViewSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'reports.view');
        const now = Date.now();
        const existing = args.replaceId
            ? await tx.run(zql.reportViews.where('id', '=', args.replaceId).one())
            : await findByTabAndName(tx, args.tab, args.name);
        if (existing) {
            const updated = {
                id: existing.id,
                name: args.name,
                filters: args.filters,
                fieldIds: args.fieldIds,
                updatedBy: ctx.user.id,
                updatedAt: now,
            };
            await tx.mutate.reportViews.update(updated);
            if (tx.location === 'server') {
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.reportView,
                    resourceId: existing.id,
                    domain: 'reports',
                    event: EventLogs.REPORT_VIEW_UPDATED,
                    action: 'update',
                    status: 'OK',
                    patches: createPatch(existing, { ...existing, ...updated }),
                    performedBy: ctx.user.id,
                    performedAt: now,
                    details: viewDetails({ ...existing, ...updated }),
                }, ctx);
            }
            return;
        }
        if (args.replaceId)
            throw new Error('Not found');
        const view = {
            id: args.id ?? uuidv7(),
            tab: args.tab,
            name: args.name,
            filters: args.filters,
            fieldIds: args.fieldIds,
            position: await nextPosition(tx, args.tab),
            createdBy: ctx.user.id,
            createdAt: now,
        };
        await tx.mutate.reportViews.insert(view);
        if (tx.location === 'server') {
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.reportView,
                resourceId: view.id,
                domain: 'reports',
                event: EventLogs.REPORT_VIEW_CREATED,
                action: 'create',
                status: 'OK',
                patches: createPatch({}, view),
                performedBy: ctx.user.id,
                performedAt: now,
                details: viewDetails(view),
            }, ctx);
        }
    }),
    update: defineMutator(UpdateReportViewSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'reports.view');
        const prev = await tx.run(zql.reportViews.where('id', '=', args.id).one());
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updated = {
            id: args.id,
            filters: args.filters,
            fieldIds: args.fieldIds,
            updatedBy: ctx.user.id,
            updatedAt: now,
        };
        await tx.mutate.reportViews.update(updated);
        if (tx.location === 'server') {
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.reportView,
                resourceId: args.id,
                domain: 'reports',
                event: EventLogs.REPORT_VIEW_UPDATED,
                action: 'update',
                status: 'OK',
                patches: createPatch(prev, { ...prev, ...updated }),
                performedBy: ctx.user.id,
                performedAt: now,
                details: viewDetails({ ...prev, ...updated }),
            }, ctx);
        }
    }),
    remove: defineMutator(DeleteReportViewSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'reports.view');
        const prev = await tx.run(zql.reportViews.where('id', '=', args.id).one());
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        // soft delete: the unique (tab, name) index only covers live rows, so the name can be reused
        const deleted = {
            id: args.id,
            deletedBy: ctx.user.id,
            deletedAt: now,
        };
        await tx.mutate.reportViews.update(deleted);
        if (tx.location === 'server') {
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.reportView,
                resourceId: args.id,
                domain: 'reports',
                event: EventLogs.REPORT_VIEW_DELETED,
                action: 'delete',
                status: 'WARNING',
                patches: createPatch(prev, { ...prev, ...deleted }),
                performedBy: ctx.user.id,
                performedAt: now,
                details: viewDetails(prev),
            }, ctx);
        }
    }),
    /** Writes the shared order for one tab: `ids` is the full order, first to last. */
    reorder: defineMutator(ReorderReportViewsSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'reports.view');
        const views = await tx.run(zql.reportViews.where('tab', '=', args.tab).where('deletedAt', 'IS', null));
        const known = new Set(views.map(view => view.id));
        const ordered = args.ids.filter(id => known.has(id));
        const now = Date.now();
        await Promise.all(ordered.map((id, position) => tx.mutate.reportViews.update({ id, position, updatedBy: ctx.user.id, updatedAt: now })));
        if (tx.location === 'server') {
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.reportView,
                resourceId: ordered[0] ?? uuidv7(),
                domain: 'reports',
                event: EventLogs.REPORT_VIEW_REORDERED,
                action: 'update',
                status: 'INFO',
                performedBy: ctx.user.id,
                performedAt: now,
                details: { tab: args.tab, order: ordered.join(',') },
            }, ctx);
        }
    }),
};
//# sourceMappingURL=report-views.mutators.js.map